import { useMutation } from "@apollo/client";
import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";

import {
  CompleteLearningLessonDocument,
  CompleteLearningLessonMutation,
  CompleteLearningLessonMutationVariables,
  StartLearningCourseDocument,
  StartLearningCourseMutation,
  StartLearningCourseMutationVariables,
  SubmitLearningQuizAttemptDocument,
  SubmitLearningQuizAttemptMutation,
  SubmitLearningQuizAttemptMutationVariables,
} from "../generated/graphql";

export type StartedLearningCourse = StartLearningCourseMutation["startLearningCourse"];
export type CompletedLearningLesson = CompleteLearningLessonMutation["completeLearningLesson"];
export type LearningQuizResult = SubmitLearningQuizAttemptMutation["submitLearningQuizAttempt"];

type StartInput = StartLearningCourseMutationVariables["input"];
type CompleteLessonInput = CompleteLearningLessonMutationVariables["input"];
type SubmitQuizInput = SubmitLearningQuizAttemptMutationVariables["input"];

export const createLearningCommandId = (): string => uuidv4();

export const useLearningCommands = () => {
  const [mutateStart, startState] = useMutation<StartLearningCourseMutation, StartLearningCourseMutationVariables>(
    StartLearningCourseDocument
  );
  const [mutateComplete, completeState] = useMutation<
    CompleteLearningLessonMutation,
    CompleteLearningLessonMutationVariables
  >(CompleteLearningLessonDocument);
  const [mutateSubmit, submitState] = useMutation<
    SubmitLearningQuizAttemptMutation,
    SubmitLearningQuizAttemptMutationVariables
  >(SubmitLearningQuizAttemptDocument);

  const startCourse = useCallback(
    async (input: StartInput): Promise<StartedLearningCourse> => {
      const result = await mutateStart({ variables: { input }, refetchQueries: ["MyLearning"] });
      if (!result.data?.startLearningCourse) throw new Error("Learning course start returned no result");
      return result.data.startLearningCourse;
    },
    [mutateStart]
  );

  const completeLesson = useCallback(
    async (input: CompleteLessonInput): Promise<CompletedLearningLesson> => {
      const result = await mutateComplete({ variables: { input }, refetchQueries: ["MyLearning"] });
      if (!result.data?.completeLearningLesson) throw new Error("Learning lesson completion returned no result");
      return result.data.completeLearningLesson;
    },
    [mutateComplete]
  );

  const submitQuiz = useCallback(
    async (input: SubmitQuizInput): Promise<LearningQuizResult> => {
      const result = await mutateSubmit({ variables: { input }, refetchQueries: ["MyLearning"] });
      if (!result.data?.submitLearningQuizAttempt) throw new Error("Learning quiz submission returned no result");
      return result.data.submitLearningQuizAttempt;
    },
    [mutateSubmit]
  );

  return {
    startCourse,
    completeLesson,
    submitQuiz,
    starting: startState.loading,
    completing: completeState.loading,
    submitting: submitState.loading,
  };
};
